import { ArrowRightOutline18Icon } from "@vygruppen/spor-icon-react";
import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { format } from "date-fns";
import { TitleSubtitleChildren } from "features/History/HistoryDetail/components/TitleSubtitleChildren";
import {
  Group,
  OriginSystem,
  useHistoryDetail,
  Visibility,
} from "features/History/HistoryDetail/fetchHistoryDetail";
import { tagFromGroup } from "features/History/HistoryDetail/HistoryDetail";
import { groupBy } from "features/VehicleSidebar/VehicleList/TrainList/TrainList";
import { FC, Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  DropsButton,
  Flex,
  Grid,
  Separator,
  Text,
  TextLink,
  VStack,
} from "shared/components/Spor";
import { AffectedStop, Distribution } from "shared/types/historyDetail";
import styled, { css, useTheme } from "styled-components";

const InnerTable = styled(Box)<{ outline?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  border-radius: 6px;
  padding: 1.5rem 0;
  ${({ outline, theme }) =>
    outline &&
    css`
      border: 1px solid ${theme.colorOutlineSecondary};
    `}
`;

export const TextOutline = styled(Text)`
  outline: 1px solid ${({ theme }) => theme.colorOutlineMain};
  padding: 6px 0.75rem 3px 0.75rem;
  margin-bottom: 6px;
  border-radius: 6px;
  width: fit-content;
`;

export type TableDetailAccordionPanelProps = {
  group: Group;
  reason: string;
  actionCard: string | undefined;
  visibility: Visibility;
  originSystem: OriginSystem;
  distributions: Distribution[];
  affectedStops: AffectedStop[] | null;
  relevantTrain: TrainIdentifier | undefined;
  relevantStops: AffectedStop[] | undefined;
  relatedEventUuid: string | undefined;
  setEventDetailUUID: (val: string) => void;
};

const DistributionContainer = styled(Grid)`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px; /* This adds some space between the two texts */
  align-items: center;
`;

export const TableDetailAccordionPanel: FC<TableDetailAccordionPanelProps> = ({
  group,
  reason,
  actionCard,
  visibility,
  originSystem,
  distributions,
  affectedStops,
  relevantTrain,
  relevantStops,
  relatedEventUuid,
  setEventDetailUUID,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const relatedEventDetails = useHistoryDetail(relatedEventUuid);
  const relatedEventDetail = relatedEventDetails?.data?.versions[0];
  return (
    <VStack flex="1" w={{ base: "100%" }}>
      <InnerTable>
        {group !== "STATION" && (
          <>
            <TitleSubtitleChildren
              row
              title={`${t("Årsak")}:`}
              subtitle={reason}
            />
            <Separator my="12px" borderColor={theme.colorOutlineMain} />
          </>
        )}

        {actionCard && (
          <>
            <TitleSubtitleChildren
              row
              title={`${t("Aksjonskort")}:`}
              subtitle={actionCard}
            />
            <Separator my="12px" borderColor={theme.colorOutlineMain} />
          </>
        )}

        <TitleSubtitleChildren row title={`${t("Opprettet av")}:`}>
          <Flex flexDirection="column">
            <Text>{originSystem.name}</Text>

            <Flex>
              <Text style={{ fontSize: "14px" }} fontWeight="normal">
                {originSystem.user ?? "-"}
              </Text>
            </Flex>
          </Flex>
        </TitleSubtitleChildren>
        <Separator my="12px" borderColor={theme.colorOutlineMain} />
        <TitleSubtitleChildren row title={`${t("Synlighet")}:`}>
          <Flex flexDirection="column">
            {visibility.fromTime && (
              <Text>
                {t("fra")} {format(visibility.fromTime, "dd.MM.yyyy - HH:mm")}
              </Text>
            )}
            {visibility.toTime && (
              <Text>
                {t("til")} {format(visibility.toTime, "dd.MM.yyyy - HH:mm")}
              </Text>
            )}
          </Flex>
        </TitleSubtitleChildren>
        <Separator my="12px" borderColor={theme.colorOutlineMain} />
        {affectedStops && (
          <>
            <TitleSubtitleChildren row title={`${t("Påvirkede stasjoner")}:`}>
              <Flex flexDirection="column">
                <Text>
                  {affectedStops
                    .map((stop) => stop.name ?? stop.jbvId)
                    .join(" - ")}
                </Text>
              </Flex>
            </TitleSubtitleChildren>
            <Separator my="12px" borderColor={theme.colorOutlineMain} />
          </>
        )}
        {relevantTrain && (
          <>
            <TitleSubtitleChildren
              row
              title={`${String(t("Tilknyttet tog"))}:`}
            >
              <Flex flexDirection="column">
                <TextLink // TODO: Legg til link dashboard/train/NO/80061/2025-06-25
                  href={`/dashboard/train/${relevantTrain.countryCode}/${relevantTrain.identifier}/${relevantTrain.nominalDate}`}
                  width="fit-content"
                >
                  {relevantTrain.identifier}
                </TextLink>
              </Flex>
            </TitleSubtitleChildren>
            <Separator my="12px" borderColor={theme.colorOutlineMain} />
          </>
        )}
        {relevantStops && (
          <>
            <TitleSubtitleChildren row title={`${t("Tilknyttet stasjoner")}:`}>
              <Flex flexDirection="column">
                <Text>
                  {relevantStops
                    .map((stop) => stop.name ?? stop.jbvId)
                    .join(" - ")}
                </Text>
              </Flex>
            </TitleSubtitleChildren>
            <Separator my="12px" borderColor={theme.colorOutlineMain} />
          </>
        )}
        {relatedEventUuid && relatedEventDetail && (
          <>
            <TitleSubtitleChildren row title={`${t("Tilknyttet hendelse")}:`}>
              <Flex flexDirection="column">
                <Flex justifyContent="space-between" alignItems="center">
                  <Flex gap="6px">
                    <TextOutline
                      padding="2px 10px 2px 0px"
                      fontSize="2xs"
                      margin="0px "
                      fontWeight="bold"
                      display="flex"
                    >
                      {tagFromGroup(t)[relatedEventDetail.type.group]}
                    </TextOutline>
                    <Flex gap="6px">
                      <Text fontWeight="bold">
                        {relatedEventDetail.type.label}
                      </Text>
                      <Text>
                        / {format(relatedEventDetail.createdAt, "dd.MM.yyyy")}
                      </Text>
                    </Flex>
                  </Flex>
                  <DropsButton
                    rightIcon={<ArrowRightOutline18Icon />}
                    variant="ghost"
                    size="xs"
                    alignSelf="center"
                    width="fit-content"
                    onClick={() => setEventDetailUUID(relatedEventUuid)}
                    id="seedetails"
                  >
                    <Text>{t("Se detaljer")}</Text>
                  </DropsButton>
                </Flex>
              </Flex>
            </TitleSubtitleChildren>
            <Separator my="12px" borderColor={theme.colorOutlineMain} />
          </>
        )}
        <TitleSubtitleChildren row title={`${t("Distribusjon")}:`}>
          <DistributionContainer gap="8px">
            {Object.values(
              groupBy(
                distributions,
                (distribution: Distribution) => distribution.text.message,
              ),
            ).map((distributionsGrouped, distributionsIndex) => {
              const contextNames = distributionsGrouped.map(
                (it) => it.contextName,
              );
              const {
                text: { message },
              } = distributionsGrouped[0]; // groupBy text, so any text will do
              return (
                <Fragment key={message}>
                  {distributionsIndex > 0 && (
                    <Separator
                      my="12px"
                      gridColumn="1 / -1"
                      borderColor={theme.colorOutlineMain}
                    />
                  )}
                  <Box display="flex" flexDirection="column" gap="4px">
                    {contextNames.map((contextName) => (
                      <TextOutline key={contextName}>{contextName}</TextOutline>
                    ))}
                  </Box>
                  <Text paddingX="1rem">{message}</Text>
                </Fragment>
              );
            })}
          </DistributionContainer>
        </TitleSubtitleChildren>
      </InnerTable>
    </VStack>
  );
};
